import { useEffect, useMemo, useRef, useState } from 'react';

import { ClusterOptions, ExtendedMarkerData, IdType, TooltipElement } from '../../types';
import usePrevious from '../../use-previous';
import {
	addGoogleMarker,
	MARKER_REPOSITION_ANIMATION_DELAY,
	MARKER_REPOSITION_ANIMATION_DELTA,
	MARKER_REPOSITION_ANIMATION_DISTANCE_LIMIT,
	removeEntityFromMap,
	setMapCenter,
	shouldDisplayTooltipForSelectedMarker
} from '../utils/utils';
import { getDataForMarker, removeDataForMarker, setDataForMarker } from '../utils/markers-to-marker-data';
import { useClusters } from './use-clusters';
import { useMapTriggers } from './use-map-triggers';

export const useGoogleMarkers = ({
	markers,
	map,
	shouldAutoFocusMap,
	onSelectMarker,
	selectedMarkers,
	forceRecenterOnAddingMarkers,
	clusterOptions = { cluster: false }
}: {
	markers: ExtendedMarkerData[];
	map: google.maps.Map | null;
	shouldAutoFocusMap: boolean;
	onSelectMarker?: (isCtrlClick: boolean, markerId: IdType) => void;
	selectedMarkers?: IdType[];
	forceRecenterOnAddingMarkers?: boolean;
	clusterOptions?: ClusterOptions;
}) => {
	const unsubscribeFromMapEvents = useRef<google.maps.MapsEventListener[]>([]);
	const [markerInstanceByIdMap, setMarkersInstanceByIdMap] = useState<
		Map<IdType, google.maps.marker.AdvancedMarkerElement>
	>(new Map());
	const [timeoutByMarkerIdMap, setTimeoutByMarkerIdMap] = useState<Map<IdType, NodeJS.Timeout>>(new Map());

	const prevSelectedMarkers: IdType[] = usePrevious(selectedMarkers) || [];
	const prevMarkers: ExtendedMarkerData[] = usePrevious(markers) || [];

	const infoWindow = useMemo(() => {
		const window = new google.maps.InfoWindow({
			content: ''
		});

		unsubscribeFromMapEvents.current.push(
			window.addListener('close', () => {
				const currentContent = window.getContent();
				(currentContent as TooltipElement)?.dispose?.();
			})
		);

		unsubscribeFromMapEvents.current.push(
			window.addListener('visible', () => {
				const currentContent = window.getContent();
				(currentContent as TooltipElement)?.onTooltipOpen?.();
			})
		);

		return window;
	}, []);

	useEffect(() => {
		if (map) {
			unsubscribeFromMapEvents.current.push(
				google.maps.event.addListener(map, 'zoom_changed', () => infoWindow.close())
			);
		}
	}, [map]);

	useEffect(() => {
		infoWindow.close();
	}, [markers?.length]);

	useMapTriggers({
		map,
		markers,
		infoWindow,
		unsubscribeFromMapEvents
	});

	const { updateMarkerInCluster } = useClusters({
		map,
		markerInstanceMap: markerInstanceByIdMap,
		infoWindow,
		clusterOptions
	});

	const removeMarker = (
		markerId: IdType,
		markerToRemove: google.maps.marker.AdvancedMarkerElement,
		timeoutsTemp?: Map<IdType, NodeJS.Timeout>
	) => {
		// Clean up the event listener before removing the marker
		const markerData = getDataForMarker(markerToRemove);
		if (markerData?.markerTriggerListener) {
			markerData.markerTriggerListener.remove();
		}

		removeEntityFromMap(markerToRemove);

		const timeoutToClear = timeoutsTemp?.get(markerId);
		timeoutToClear && clearTimeout(timeoutToClear);
	};

	const getMarkersToRedraw = (): IdType[] => {
		const selectedMarkersToRedraw = prevSelectedMarkers
			.filter(x => !(selectedMarkers || []).includes(x))
			.concat((selectedMarkers || []).filter(x => !prevSelectedMarkers.includes(x)));

		const dataMarkersToRedraw = markers
			.filter(marker => {
				const prevMarker = prevMarkers.find(m => m.id === marker.id);
				return prevMarker && (prevMarker.color !== marker.color || prevMarker?.text !== marker.text);
			})
			.map(({ id }) => id);

		return [...selectedMarkersToRedraw, ...dataMarkersToRedraw];
	};

	const redrawSelectedMarkers = (markerInstanceMap: Map<IdType, google.maps.marker.AdvancedMarkerElement>) => {
		if (!map || (!markers && !selectedMarkers)) return;
		const markersToRedraw = getMarkersToRedraw();

		const currentlyOpenMarkerId = infoWindow.anchor
			? getDataForMarker(infoWindow.anchor as google.maps.marker.AdvancedMarkerElement)?.id
			: undefined;

		markersToRedraw?.forEach(markerId => {
			const markerToRemove = markerInstanceByIdMap.get(markerId);
			if (markerToRemove) {
				removeMarker(markerId, markerToRemove);
				removeDataForMarker(markerToRemove);
			}

			const markerIndex = markers.findIndex(m => m.id === markerId);
			const markerData = { ...markers[markerIndex] };
			const googleMarker = addGoogleMarker({
				map,
				markerData: markerData,
				infoWindow,
				onSelectMarker,
				selectedMarkers,
				hideAnimation: true,
				shouldOpenTooltipOnCreation:
					currentlyOpenMarkerId === markerId &&
					shouldDisplayTooltipForSelectedMarker(selectedMarkers, markerId)
			});
			if (googleMarker) {
				markerInstanceMap.set(markerId, googleMarker);
			}
		});
	};

	const isMarkersMapsListDifferent = (newMarkersMap: Map<IdType, google.maps.marker.AdvancedMarkerElement>) => {
		if (newMarkersMap.size !== markerInstanceByIdMap.size) {
			return true;
		}

		for (const [key, value] of newMarkersMap) {
			if (!markerInstanceByIdMap.has(key) || markerInstanceByIdMap.get(key) !== value) {
				return true;
			}
		}

		return false;
	};

	useEffect(() => {
		if (!map || (!markers && !markerInstanceByIdMap.size)) return;

		const temp = new Map(markerInstanceByIdMap);
		const timeoutsTemp = new Map(timeoutByMarkerIdMap);
		const markerIdsToRemove = new Set(temp.keys());

		if (markers?.length) {
			markers.forEach((m: ExtendedMarkerData) => {
				if (!m) return;

				let markerInstance = temp.get(m.id);
				if (!markerInstance) {
					markerInstance = addGoogleMarker({
						map,
						markerData: m,
						infoWindow,
						onSelectMarker,
						selectedMarkers
					});
					if (!markerInstance) {
						return;
					}

					temp.set(m.id, markerInstance);
					return;
				}

				setDataForMarker(markerInstance, m);

				markerIdsToRemove.delete(m.id);

				if (
					markerInstance.position?.lat !== m.location.lat ||
					markerInstance.position?.lng !== m.location.lng
				) {
					const moveToLatLng = new google.maps.LatLng(m.location.lat, m.location.lng);
					const markerPosition = markerInstance?.position;

					if (
						markerPosition &&
						isMoveToLocationTooFar(markerPosition as google.maps.LatLngAltitude, moveToLatLng)
					) {
						const timeoutInstance = timeoutsTemp.get(m.id);
						timeoutInstance && clearTimeout(timeoutInstance);
						markerInstance.position = moveToLatLng;
						if (shouldAutoFocusMap && m.shouldAutoFocus) {
							setMapCenter(map, m.location);
						}
						updateMarkerInCluster(markerInstance);
					} else {
						const markerAnimation = runMoveMarkerAnimation(markerInstance, m, map, shouldAutoFocusMap, () =>
							updateMarkerInCluster(markerInstance)
						);
						markerAnimation && timeoutsTemp.set(m.id, markerAnimation);
					}
				}
			});
		}

		markerIdsToRemove.forEach(id => {
			const markerToRemove = temp.get(id);
			markerToRemove && removeMarker(id, markerToRemove, timeoutsTemp);
			temp.delete(id);
		});

		redrawSelectedMarkers(temp);

		if (isMarkersMapsListDifferent(temp)) {
			setMarkersInstanceByIdMap(temp);
		}
		setTimeoutByMarkerIdMap(timeoutsTemp);

		return () => timeoutByMarkerIdMap.forEach(value => value && clearTimeout(value));
	}, [map, markers, shouldAutoFocusMap, selectedMarkers]);

	useEffect(() => {
		if (map && forceRecenterOnAddingMarkers && !shouldAutoFocusMap) {
			markers.forEach(m => {
				if (!m) {
					return;
				}
				setMapCenter(map, m.location);
			});
		}
	}, [markers?.length]);

	useEffect(() => {
		return () => {
			unsubscribeFromMapEvents.current.forEach(listener => {
				listener.remove();
			});
			unsubscribeFromMapEvents.current = [];
		};
	}, []);

	return markerInstanceByIdMap;
};

const runMoveMarkerAnimation = (
	marker: google.maps.marker.AdvancedMarkerElement,
	markerData: ExtendedMarkerData,
	map: google.maps.Map,
	shouldAutoFocusMap: boolean,
	onAnimationComplete?: () => void
) => {
	let timeoutInstance;
	const current = marker.position;
	const moveTo = new google.maps.LatLng(markerData.location.lat, markerData.location.lng);

	const deltalat =
		(moveTo.lat() - (typeof current?.lat === 'function' ? current.lat() : current?.lat || 0)) /
		MARKER_REPOSITION_ANIMATION_DELTA;
	const deltalng =
		(moveTo.lng() - (typeof current?.lng === 'function' ? current.lng() : current?.lng || 0)) /
		MARKER_REPOSITION_ANIMATION_DELTA;

	const isLastStep = (stepIndex: number) => stepIndex === MARKER_REPOSITION_ANIMATION_DELTA - 1;

	for (let i = 0; i < MARKER_REPOSITION_ANIMATION_DELTA; i++) {
		timeoutInstance = setTimeout(() => {
			let lat = typeof marker.position?.lat === 'function' ? marker.position.lat() : marker.position?.lat || 0;
			let lng = typeof marker.position?.lng === 'function' ? marker.position.lng() : marker.position?.lng || 0;
			lat += deltalat;
			lng += deltalng;
			const latlng = new google.maps.LatLng(lat, lng);
			marker.position = latlng;
			if (shouldAutoFocusMap && markerData.shouldAutoFocus) {
				setMapCenter(map, latlng);
			}

			if (isLastStep(i)) {
				onAnimationComplete?.();
			}
		}, MARKER_REPOSITION_ANIMATION_DELAY * i);
	}

	return timeoutInstance;
};

const isMoveToLocationTooFar = (current: google.maps.LatLngAltitude, moveTo: google.maps.LatLng) => {
	const latSorted = [moveTo.lat(), current.lat].sort((a, b) => b - a);
	const lngSorted = [moveTo.lng(), current.lng].sort((a, b) => b - a);

	const latSum = latSorted[0] - latSorted[1];
	const lngSum = lngSorted[0] - lngSorted[1];

	const distanceSum = latSum + lngSum;
	return distanceSum > MARKER_REPOSITION_ANIMATION_DISTANCE_LIMIT;
};
